import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Coins, 
  TrendingUp, 
  Circle, 
  Spade, 
  Gift, 
  User, 
  LogOut, 
  Menu, 
  X,
  Link as LinkIcon,
  Shield,
  Wallet,
  ArrowUpRight,
  Settings,
  Unlink,
  ChevronDown,
  Bomb,
  Building2,
  Crown
} from 'lucide-react';
import BalanceDisplay from './BalanceDisplay';
import ChatSidebar from './ChatSidebar';
import SupportWidget from './SupportWidget';

const API = process.env.REACT_APP_BACKEND_URL;

const gameItems = [
  { path: '/coinflip', label: 'Coinflip', icon: Coins },
  { path: '/crash', label: 'Crash', icon: TrendingUp },
  { path: '/roulette', label: 'Roulette', icon: Circle },
  { path: '/blackjack', label: 'Blackjack', icon: Spade },
  { path: '/mines', label: 'Mines', icon: Bomb },
  { path: '/towers', label: 'Towers', icon: Building2 },
  { path: '/chess', label: 'Chess', icon: Crown },
  { path: '/cases', label: 'Cases', icon: Gift },
];

export default function Layout() {
  const { user, token, logout, loading, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const handleUnlinkRoblox = async () => {
    try {
      await axios.post(
        `${API}/api/roblox/unlink`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Roblox account unlinked');
      refreshUser?.();
      setUserMenuOpen(false);
    } catch (error) {
      toast.error('Failed to unlink account');
    }
  };

  const isLoggedIn = !!user;

  return (
    <div className="min-h-screen bg-[#020617]" data-rbx="go fuck yourself">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - positioned more to the left */}
            <Link to="/" className="flex items-center gap-2 -ml-4 lg:-ml-16" data-testid="header-logo">
              <span className="font-display text-xl font-bold">
                <span className="text-white">RBX</span>
                <span className="text-red-500">CASINO</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {gameItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon size={18} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Right section */}
            <div className="flex items-center gap-4">
              {isLoggedIn ? (
                <>
                  <BalanceDisplay />

                  {/* User menu */}
                  <div className="hidden md:flex items-center gap-2">
                    <Link
                      to="/deposit"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600/20 text-green-400 text-xs font-medium hover:bg-green-600/30 transition-colors"
                    >
                      <Wallet size={14} />
                      Deposit
                    </Link>
                    <Link
                      to="/withdraw"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600/20 text-red-400 text-xs font-medium hover:bg-red-600/30 transition-colors"
                    >
                      <ArrowUpRight size={14} />
                      Withdraw
                    </Link>
                    
                    {/* User Dropdown */}
                    <div className="relative" ref={userMenuRef}>
                      <button
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                        className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-800 transition-colors"
                      >
                        {user?.roblox_avatar_url ? (
                          <img
                            src={user.roblox_avatar_url}
                            alt="Avatar"
                            className="w-8 h-8 rounded-full border-2 border-blue-500/50"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                            <User size={16} className="text-slate-400" />
                          </div>
                        )}
                        <span className="text-sm text-slate-300">{user?.username}</span>
                        <ChevronDown size={14} className={`text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {userMenuOpen && (
                        <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-slate-900 border border-slate-700 shadow-xl py-2 z-50">
                          {/* User Info */}
                          <div className="px-4 py-3 border-b border-slate-700">
                            <p className="text-white font-medium">{user?.username}</p>
                            {user?.roblox_username && (
                              <p className="text-slate-400 text-sm">@{user.roblox_username}</p>
                            )}
                          </div>

                          {/* Menu Items */}
                          <div className="py-2">
                            <Link
                              to="/profile"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                            >
                              <User size={16} />
                              Profile
                            </Link>
                            
                            <Link
                              to="/deposit"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                            >
                              <Wallet size={16} />
                              Manage Robux
                            </Link>

                            {user?.roblox_username ? (
                              <>
                                <Link
                                  to="/link-roblox"
                                  onClick={() => setUserMenuOpen(false)}
                                  className="flex items-center gap-3 px-4 py-2 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                                >
                                  <Settings size={16} />
                                  Roblox Settings
                                </Link>
                                <button
                                  onClick={handleUnlinkRoblox}
                                  className="flex items-center gap-3 px-4 py-2 w-full text-left text-red-400 hover:bg-red-500/10 transition-colors"
                                >
                                  <Unlink size={16} />
                                  Unlink Roblox
                                </button>
                              </>
                            ) : (
                              <Link
                                to="/link-roblox"
                                onClick={() => setUserMenuOpen(false)}
                                className="flex items-center gap-3 px-4 py-2 text-blue-400 hover:bg-blue-500/10 transition-colors"
                              >
                                <LinkIcon size={16} />
                                Link Roblox
                              </Link>
                            )}
                          </div>

                          {/* Logout */}
                          <div className="border-t border-slate-700 pt-2">
                            <button
                              onClick={handleLogout}
                              className="flex items-center gap-3 px-4 py-2 w-full text-left text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                            >
                              <LogOut size={16} />
                              Logout
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="hidden md:flex items-center gap-3">
                  <Link
                    to="/login"
                    className="px-4 py-2 rounded-lg text-slate-300 hover:text-white transition-colors"
                    data-testid="nav-login"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors"
                    data-testid="nav-register"
                  >
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-slate-900/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-2">
              {gameItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-blue-600/20 text-blue-400'
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon size={20} />
                    {item.label}
                  </Link>
                );
              })}
              <Link
                to="/provably-fair"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  location.pathname === '/provably-fair'
                    ? 'bg-blue-600/20 text-blue-400'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Shield size={20} />
                Provably Fair
              </Link>
              <hr className="border-white/10 my-2" />
              {isLoggedIn ? (
                <>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    <User size={20} />
                    Profile
                  </Link>
                  {!user?.roblox_username && (
                    <Link
                      to="/link-roblox"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-blue-400 hover:bg-blue-600/10"
                    >
                      <LinkIcon size={20} />
                      Link Roblox Account
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 w-full"
                  >
                    <LogOut size={20} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    <User size={20} />
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-blue-400 hover:bg-blue-600/10"
                  >
                    <User size={20} />
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main layout with chat sidebar */}
      <div className="flex pt-16 min-h-screen">
        {/* Chat Sidebar - Fixed on left */}
        <div className="hidden lg:block fixed left-0 top-16 bottom-0 z-40">
          <ChatSidebar />
        </div>

        {/* Main content - offset for sidebar on large screens */}
        <main className="flex-1 lg:ml-72 py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Support Widget - Fixed bottom right */}
      <SupportWidget />
    </div>
  );
}
